import React, { useEffect, useState } from "react";
import { Plus, Loader2, Check, EyeOff, Eye, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { confirmAsync } from "@/lib/safeConfirm";
import { usePartners } from "@/lib/partners";
import { useAuth } from "@/lib/auth";

const inputCls = "bg-white border border-clay-300 px-2 py-1.5 text-sm outline-none focus:border-terracotta w-full";
const num = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));

function PartnerRow({ p, sofiPartnerOptions, sofiSourceOptions, onSaved }) {
  const [form, setForm] = useState(p);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(p), [p]);
  const dirty = ["label", "commission_pct", "markup_pct", "sofi_partner", "sofi_source"].some((k) => String(form[k] ?? "") !== String(p[k] ?? ""));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/partners/${p.value}`, {
        label: form.label, commission_pct: num(form.commission_pct), markup_pct: num(form.markup_pct),
        sofi_partner: form.sofi_partner, sofi_source: form.sofi_source, ...extra,
      });
      toast.success(`"${form.label}" guardado`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!(await confirmAsync(`¿Borrar el partner "${p.label}"?`, { destructive: true, confirmLabel: "Borrar" }))) return;
    try { await api.delete(`/partners/${p.value}`); toast.success("Partner borrado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };

  return (
    <tr className={`border-t border-clay-200 ${p.hidden ? "opacity-60" : ""}`} data-testid={`partner-row-${p.value}`}>
      <td className="px-3 py-2 w-10 text-center">
        <button onClick={() => !p.isDefault && save({ is_default: true })} disabled={saving} data-testid={`partner-default-${p.value}`}
          title={p.isDefault ? "Partner por defecto al crear un itinerario" : "Usar como partner por defecto al crear itinerarios"}
          className={`p-1.5 transition-colors ${p.isDefault ? "text-terracotta" : "text-clay-300 hover:text-clay-700"}`}>
          <Star size={14} fill={p.isDefault ? "currentColor" : "none"}/>
        </button>
      </td>
      <td className="px-3 py-2"><input data-testid={`partner-label-${p.value}`} className={inputCls} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}/></td>
      <td className="px-3 py-2 w-28"><div className="flex items-center gap-1"><input data-testid={`partner-commission-${p.value}`} type="number" step="0.01" min="0" max="60" className={`${inputCls} text-right tabular`} value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })}/><span className="text-xs text-clay-600">%</span></div></td>
      <td className="px-3 py-2 w-28"><div className="flex items-center gap-1"><input data-testid={`partner-markup-${p.value}`} type="number" step="0.5" min="0" max="200" className={`${inputCls} text-right tabular`} value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })}/><span className="text-xs text-clay-600">%</span></div></td>
      <td className="px-3 py-2 w-44">
        <select className={inputCls} value={form.sofi_partner || "Direct"} onChange={(e) => setForm({ ...form, sofi_partner: e.target.value })} data-testid={`partner-sofi-partner-${p.value}`}>
          {sofiPartnerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 w-44">
        <select className={inputCls} value={form.sofi_source || "Direct"} onChange={(e) => setForm({ ...form, sofi_source: e.target.value })} data-testid={`partner-sofi-source-${p.value}`}>
          {sofiSourceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-right">
        <div className="inline-flex items-center gap-1">
          <button onClick={() => save()} disabled={!dirty || saving} data-testid={`partner-save-${p.value}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] uppercase tracking-wider ${dirty ? "bg-clay-900 text-white hover:bg-terracotta" : "border border-clay-200 text-clay-400"}`}>
            {saving ? <Loader2 size={11} className="animate-spin"/> : <Check size={11}/>} Guardar
          </button>
          <button onClick={() => save({ hidden: !p.hidden })} data-testid={`partner-toggle-${p.value}`} title={p.hidden ? "Mostrar en el desplegable" : "Ocultar del desplegable (los itinerarios que lo usan no cambian)"}
            className="p-1.5 border border-clay-300 text-clay-700 hover:border-clay-900">
            {p.hidden ? <Eye size={12}/> : <EyeOff size={12}/>}
          </button>
          {!p.builtin && (
            <button onClick={remove} data-testid={`partner-delete-${p.value}`} title="Borrar (solo si ningún itinerario lo usa)" className="p-1.5 border border-clay-300 text-clay-700 hover:text-destructive hover:border-destructive"><Trash2 size={12}/></button>
          )}
        </div>
      </td>
    </tr>
  );
}

function AddPartner({ sofiPartnerOptions, sofiSourceOptions, onAdded }) {
  const blank = { label: "", commission_pct: "", markup_pct: 30, sofi_partner: "Direct", sofi_source: "Travel Agency" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!form.label.trim()) { toast.error("Pon un nombre"); return; }
    setSaving(true);
    try {
      await api.post("/partners", { ...form, commission_pct: num(form.commission_pct) || 0, markup_pct: num(form.markup_pct) });
      toast.success(`Partner "${form.label}" creado`);
      setForm(blank);
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-clay-300 bg-white p-5 mb-8" data-testid="add-partner">
      <div className="smallcaps mb-3">Añadir partner</div>
      <div className="grid grid-cols-1 md:grid-cols-[1.4fr_120px_120px_1fr_1fr_auto] gap-2 items-end">
        <label className="block"><span className="text-[11px] text-clay-600 block mb-1">Nombre</span><input data-testid="add-partner-label" className={inputCls} placeholder="p. ej. SA Expeditions" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}/></label>
        <label className="block"><span className="text-[11px] text-clay-600 block mb-1">Comisión %</span><input data-testid="add-partner-commission" type="number" step="0.01" min="0" max="60" className={`${inputCls} text-right tabular`} placeholder="10" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })}/></label>
        <label className="block"><span className="text-[11px] text-clay-600 block mb-1">Markup %</span><input data-testid="add-partner-markup" type="number" step="0.5" min="0" max="200" className={`${inputCls} text-right tabular`} value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })}/></label>
        <label className="block"><span className="text-[11px] text-clay-600 block mb-1">Partner en Sofi</span>
          <select className={inputCls} value={form.sofi_partner} onChange={(e) => setForm({ ...form, sofi_partner: e.target.value })} data-testid="add-partner-sofi-partner">
            {sofiPartnerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="block"><span className="text-[11px] text-clay-600 block mb-1">Source en Sofi</span>
          <select className={inputCls} value={form.sofi_source} onChange={(e) => setForm({ ...form, sofi_source: e.target.value })} data-testid="add-partner-sofi-source">
            {sofiSourceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <button onClick={add} disabled={saving} data-testid="add-partner-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-clay-900 text-white text-[12px] tracking-wider uppercase hover:bg-terracotta disabled:opacity-50">
          {saving ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>} Añadir
        </button>
      </div>
      <p className="text-[11px] text-clay-600 mt-3">
        La comisión es el % que se lleva el partner; el markup es el margen de la agencia sobre coste. Se aplican al elegir el partner en un itinerario nuevo (y con el botón ↻ Auto en los existentes).
        "Partner/Source en Sofi" son las opciones equivalentes en Sofi al enviar el viaje.
      </p>
    </div>
  );
}

export default function Partners() {
  const { user } = useAuth();
  const { options, canManage, loaded, sofiPartnerOptions, sofiSourceOptions, reload } = usePartners();
  const allowed = user?.role === "admin" || user?.can_manage_sources || canManage;

  return (
    <div className="px-8 py-8 max-w-6xl" data-testid="partners-page">
      <div className="mb-6">
        <div className="smallcaps">Trabajo</div>
        <h1 className="font-serif text-5xl leading-none mt-3">Partners</h1>
        <p className="text-sm text-clay-700 mt-3 max-w-2xl">
          De dónde viene cada cliente (Zicasso, agencias, directo…) y la comisión y el markup que se aplican por defecto al crear un itinerario con ese partner.
        </p>
      </div>

      {!loaded ? (
        <div className="flex items-center gap-2 text-sm text-clay-600"><Loader2 size={14} className="animate-spin"/> Cargando…</div>
      ) : !allowed ? (
        <div className="border border-dashed border-clay-300 p-8 text-center text-sm text-clay-700" data-testid="partners-forbidden">
          No tienes permiso para gestionar los partners. Pídeselo a un administrador (Usuarios &amp; Acceso → Partners).
        </div>
      ) : (
        <>
          <AddPartner sofiPartnerOptions={sofiPartnerOptions} sofiSourceOptions={sofiSourceOptions} onAdded={reload}/>
          <div className="border border-clay-300 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-clay-100 border-b border-clay-300 text-left text-[11px] uppercase tracking-wider text-clay-700">
                <tr>
                  <th className="px-3 py-2 font-medium" title="Partner por defecto">★</th>
                  <th className="px-3 py-2 font-medium">Partner</th>
                  <th className="px-3 py-2 font-medium">Comisión</th>
                  <th className="px-3 py-2 font-medium">Markup</th>
                  <th className="px-3 py-2 font-medium">Partner en Sofi</th>
                  <th className="px-3 py-2 font-medium">Source en Sofi</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {options.map((p) => (
                  <PartnerRow key={p.value} p={p} sofiPartnerOptions={sofiPartnerOptions} sofiSourceOptions={sofiSourceOptions} onSaved={reload}/>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-clay-600 mt-3">La estrella marca el partner que sale preseleccionado al crear un itinerario nuevo (un solo partner puede serlo). Los partners ocultos (atenuados) no salen en el desplegable de itinerarios nuevos, pero los viajes que ya los usan no cambian. Cambiar la comisión por defecto no modifica itinerarios existentes.</p>
        </>
      )}
    </div>
  );
}
